import React, { useEffect, useState } from 'react';
import { Linking, Text, TextInput, View } from 'react-native';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  OperationalFilterChipGroup,
  OperationalFilterFieldLabel,
  OperationalFilterSectionDivider,
  OperationalFilterSegmentedRow,
} from '../../../shared/filters/OperationalFilterPrimitives';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import type { BranchSettingsScreenViewModel } from '../useBranchSettingsScreen';
import { LABEL_SCALE_ADAPTERS } from '../../../scale/domain/labelScaleExport';
import { exportLabelScalePlu } from '../../../scale/application/exportLabelScalePlu';
import { fetchLiveScaleWeight, fetchScaleAgentHealth } from '../../../scale/infra/fetchLiveScaleWeight';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import type { LabelScaleAdapterId } from '../../../scale/domain/labelScaleExport';
import type { BranchScaleBarcodeProfile } from '../../../shell/session/store/sessionTypes';
import { PRINT_AGENT_DOWNLOAD_URL } from '../../../printing/public-api';
import { SettingsSaveCta, StatusBadge, hubStyles } from './settingsHubUi';

const SCALE_BARCODE_OPTIONS: Array<{ value: BranchScaleBarcodeProfile; label: string }> = [
  { value: 'gs1_prefix', label: 'Sin etiquetas' },
  { value: 'ean13_255_price_pesos', label: 'Precio en pesos' },
  { value: 'ean13_255_weight_grams', label: 'Peso en kilos' },
];

const SCALE_CONNECTION_OPTIONS = [
  { value: 'mock', label: 'Modo de prueba', accessibilityLabel: 'Modo de prueba, sin balanza física' },
  { value: 'serial', label: 'Balanza serial', accessibilityLabel: 'Balanza conectada por puerto serial' },
] as const;

/**
 * Balanza — etiquetas, peso en vivo por COM vía agente y archivo PLU
 * para la etiquetadora. Movido desde Reglas del POS sin cambios de lógica.
 */
export function ScaleCard({ vm, layoutWide }: { vm: BranchSettingsScreenViewModel; layoutWide: boolean }) {
  const [comPortDraft, setComPortDraft] = useState(vm.scaleConfig.comPort);
  const [agentUrlDraft, setAgentUrlDraft] = useState(vm.scaleConfig.agentUrl);
  const [probeBusy, setProbeBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  useEffect(() => {
    setComPortDraft(vm.scaleConfig.comPort);
    setAgentUrlDraft(vm.scaleConfig.agentUrl);
  }, [vm.scaleConfig.comPort, vm.scaleConfig.agentUrl, vm.currentBranch?.id]);

  const scaleDirty =
    comPortDraft !== vm.scaleConfig.comPort ||
    (agentUrlDraft.trim() || 'http://127.0.0.1:9310') !== vm.scaleConfig.agentUrl;
  const scaleConnectionUi = vm.scaleConfig.protocol === 'mock' ? 'mock' : 'serial';

  const handleScaleConnectionChange = (value: 'mock' | 'serial') => {
    if (value === 'mock') {
      vm.handleScaleProtocolChange('mock');
      return;
    }
    vm.handleScaleProtocolChange(vm.scaleConfig.protocol === 'mock' ? 'toledo' : vm.scaleConfig.protocol);
  };

  const handleProbeScale = async () => {
    if (probeBusy) return;
    setProbeBusy(true);
    try {
      const draftConfig = {
        ...vm.scaleConfig,
        comPort: comPortDraft,
        agentUrl: agentUrlDraft.trim() || 'http://127.0.0.1:9310',
      };
      const health = await fetchScaleAgentHealth(draftConfig);
      if (!health.ok) {
        showError('Programa de conexión', health.error);
        return;
      }
      if (draftConfig.protocol !== 'mock' && health.serialportAvailable === false) {
        showError(
          'Falta el soporte de balanza',
          'Descargá e instalá el paquete completo del agente de Vuelto para leer una balanza real.',
        );
        return;
      }
      const weight = await fetchLiveScaleWeight(draftConfig);
      if (!weight.ok) {
        showInfo(
          'Programa conectado, sin peso',
          `${health.service} en :${health.port}. ${weight.error}`,
        );
        return;
      }
      const kgLabel = weight.reading.kg.toLocaleString('es-AR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });
      showSuccess(
        'Balanza conectada',
        `${kgLabel} kg${weight.reading.stable ? ' · estable' : ''}`,
      );
    } finally {
      setProbeBusy(false);
    }
  };

  const handleExport = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      const result = await exportLabelScalePlu(vm.scaleConfig.labelExportAdapter);
      if (!result.ok) {
        showError('Archivo de etiquetas', result.error);
        return;
      }
      showSuccess(
        'Archivo listo',
        `${result.count} PLU en ${result.filename}. Cargalo en iTegra o Qendra.`,
      );
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <PaperSheet padded testID="branch.scale.config">
      <View style={hubStyles.cardTitleRow}>
        <Text style={styles.cardTitle}>Balanza</Text>
        <StatusBadge
          tone={vm.scaleConfig.enabled ? 'ok' : 'muted'}
          label={vm.scaleConfig.enabled ? 'Peso en vivo: activo' : 'Peso en vivo: inactivo'}
        />
      </View>

      <View style={hubStyles.controlBlock} testID="branch.codes.scale-profile">
        <OperationalFilterFieldLabel>Código de la etiqueta</OperationalFilterFieldLabel>
        <Text style={hubStyles.hintSecondary}>Elegí qué dato trae el código de la etiqueta.</Text>
        <OperationalFilterChipGroup
          ticketTone
          layout="hug"
          options={SCALE_BARCODE_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            accessibilityLabel: `Código de etiqueta: ${opt.label}`,
          }))}
          value={vm.scaleBarcodeProfile}
          disabled={!vm.canEditPosRules}
          onChange={(id) => {
            void vm.handleSaveScaleBarcodeProfile(id as BranchScaleBarcodeProfile);
          }}
        />
        <Text style={hubStyles.hintMuted} testID="branch.codes.scale-trial">
          Probá con una etiqueta de 1 kg en el POS. Si muestra otro peso, elegí otro formato. ¿No coincide?
          Escribinos con una foto de la etiqueta.
        </Text>
      </View>

      <OperationalFilterSectionDivider />

      <View style={hubStyles.controlBlock}>
        <OperationalFilterFieldLabel>Peso en vivo</OperationalFilterFieldLabel>
        <Text style={hubStyles.hintSecondary}>
          Usá una balanza conectada para cargar el peso automáticamente al cobrar.
        </Text>
        <OperationalFilterSegmentedRow
          ticketTone
          layout="hug"
          options={[
            { value: 'off', label: 'No usar', accessibilityLabel: 'No usar balanza conectada' },
            { value: 'on', label: 'Usar balanza', accessibilityLabel: 'Usar balanza conectada' },
          ]}
          value={vm.scaleConfig.enabled ? 'on' : 'off'}
          onChange={(v) => {
            if (!vm.canEditPosRules || vm.scaleConfigSaving) return;
            void vm.handleSaveScaleConfig({ enabled: v === 'on' });
          }}
        />
      </View>

      {vm.scaleConfig.enabled ? (
        <>
          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Formato de conexión</OperationalFilterFieldLabel>
            <Text style={hubStyles.hintSecondary}>
              Elegí cómo envía el peso tu balanza. Compatible con formatos seriales comunes.
            </Text>
            <OperationalFilterChipGroup
              ticketTone
              layout="hug"
              options={SCALE_CONNECTION_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
                accessibilityLabel: option.accessibilityLabel,
              }))}
              value={scaleConnectionUi}
              disabled={!vm.canEditPosRules}
              onChange={(id) => handleScaleConnectionChange(id as 'mock' | 'serial')}
            />
          </View>

          {vm.scaleConfig.protocol !== 'mock' ? (
            <View style={[hubStyles.controlBlock, layoutWide && hubStyles.inputBlockWide]}>
              <OperationalFilterFieldLabel>Puerto COM</OperationalFilterFieldLabel>
              <TextInput
                style={hubStyles.inputTicket}
                value={comPortDraft}
                onChangeText={(t) => setComPortDraft(t.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="COM3"
                placeholderTextColor={ticketColors.ink3}
                editable={vm.canEditPosRules && !vm.scaleConfigSaving}
                autoCapitalize="characters"
                testID="branch.scale.com-port"
              />
            </View>
          ) : null}

          {vm.scaleConfig.protocol !== 'mock' ? (
            <>
              <View style={hubStyles.controlBlock}>
                <OperationalFilterFieldLabel>Programa de conexión</OperationalFilterFieldLabel>
                <Text style={hubStyles.hintSecondary}>
                  El peso en vivo usa el mismo agente que la impresión. Descargalo e instalalo en esta computadora.
                </Text>
                <SettingsSaveCta
                  label="Descargar agente"
                  layoutWide={layoutWide}
                  variant="secondary"
                  onPress={() => void Linking.openURL(PRINT_AGENT_DOWNLOAD_URL)}
                  testID="branch.scale.agent-download"
                />
              </View>

              <View style={[hubStyles.controlBlock, layoutWide && hubStyles.inputBlockWide]}>
                <OperationalFilterFieldLabel>Dirección del programa</OperationalFilterFieldLabel>
                <Text style={hubStyles.hintMuted}>
                  Dejá la dirección predeterminada. La balanza tiene que estar conectada a esta computadora.
                </Text>
                <TextInput
                  style={hubStyles.inputTicket}
                  value={agentUrlDraft}
                  onChangeText={setAgentUrlDraft}
                  placeholder="http://127.0.0.1:9310"
                  placeholderTextColor={ticketColors.ink3}
                  editable={vm.canEditPosRules && !vm.scaleConfigSaving}
                  autoCapitalize="none"
                  testID="branch.scale.agent-url"
                />
              </View>
            </>
          ) : null}

          <View style={hubStyles.controlBlock}>
            <SettingsSaveCta
              label={probeBusy ? 'Leyendo peso…' : 'Leer peso'}
              layoutWide={layoutWide}
              variant="secondary"
              onPress={() => void handleProbeScale()}
              disabled={probeBusy}
              testID="branch.scale.probe"
            />
          </View>

          {vm.canEditPosRules ? (
            <SettingsSaveCta
              label="Guardar balanza"
              saving={vm.scaleConfigSaving}
              layoutWide={layoutWide}
              onPress={() =>
                void vm.handleSaveScaleConfig({
                  comPort: comPortDraft,
                  agentUrl: agentUrlDraft.trim() || 'http://127.0.0.1:9310',
                })
              }
              disabled={vm.scaleConfigSaving || !scaleDirty}
            />
          ) : null}
        </>
      ) : null}

      <OperationalFilterSectionDivider />

      <View style={hubStyles.controlBlock} testID="branch.scale.label-export">
        <OperationalFilterFieldLabel>Archivo de etiquetas</OperationalFilterFieldLabel>
        <Text style={hubStyles.hintSecondary}>
          Descargá el archivo para cargar productos y precios en la etiquetadora.
        </Text>
        <OperationalFilterSegmentedRow
          ticketTone
          layout="hug"
          options={[
            { value: 'off', label: 'No exportar', accessibilityLabel: 'No exportar archivo de etiquetas' },
            { value: 'on', label: 'Exportar archivo', accessibilityLabel: 'Exportar archivo de etiquetas' },
          ]}
          value={vm.scaleConfig.labelScaleSyncEnabled ? 'on' : 'off'}
          onChange={(v) => {
            if (!vm.canEditPosRules || vm.scaleConfigSaving) return;
            void vm.handleSaveScaleConfig({ labelScaleSyncEnabled: v === 'on' });
          }}
        />
      </View>

      {vm.scaleConfig.labelScaleSyncEnabled ? (
        <>
          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Formato del archivo</OperationalFilterFieldLabel>
            <OperationalFilterChipGroup
              ticketTone
              layout="hug"
              options={LABEL_SCALE_ADAPTERS.map((a) => ({
                value: a.id,
                label: a.brandAliases[0] ?? a.label,
                accessibilityLabel: a.label,
              }))}
              value={vm.scaleConfig.labelExportAdapter}
              disabled={!vm.canEditPosRules}
              onChange={(id) => {
                void vm.handleSaveScaleConfig({
                  labelExportAdapter: id as LabelScaleAdapterId,
                });
              }}
            />
          </View>

          <SettingsSaveCta
            label={exportBusy ? 'Preparando archivo…' : 'Descargar archivo'}
            layoutWide={layoutWide}
            variant="secondary"
            onPress={() => void handleExport()}
            disabled={exportBusy}
            testID="branch.scale.label-export.download"
          />
        </>
      ) : null}
    </PaperSheet>
  );
}
